import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Clock, Instagram, Youtube, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const ContactPage = () => {
  const [formLoaded, setFormLoaded] = useState(false);

  useEffect(() => {
    // Load HubSpot script dynamically using the exact embed code format
    const loadHubSpotScript = () => {
      // Check if script already exists
      if (document.querySelector('script[src*="js-na3.hsforms.net"]')) {
        setFormLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js-na3.hsforms.net/forms/embed/342909001.js';
      script.defer = true;
      script.onload = () => {
        setFormLoaded(true);
      };
      script.onerror = () => {
        setFormLoaded(true); // Still show container
      };
      document.body.appendChild(script);
    };

    loadHubSpotScript();

    // Fallback timeout
    const timeout = setTimeout(() => {
      setFormLoaded(true);
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  const contactInfo = [
    {
      icon: <Mail className="h-6 w-6" />,
      title: 'Email Us',
      content: 'info@manomay.ca',
      link: 'mailto:info@manomay.ca',
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Call or WhatsApp',
      content: '+1 647 575 3508',
      link: 'tel:+16475753508',
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: 'Location',
      content: 'Serving clients across Canada',
      link: null,
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: 'Response Time',
      content: '24-48 hours',
      link: null,
    },
  ];

  const socialLinks = [
    {
      icon: <Instagram className="h-6 w-6" />,
      name: 'Instagram',
      handle: '@manomay_tax',
      link: 'https://www.instagram.com/manomay_tax',
      color: 'hover:bg-pink-50 hover:text-pink-600',
    },
    {
      icon: <Youtube className="h-6 w-6" />,
      name: 'YouTube',
      handle: '@manomaytaxinc',
      link: 'https://youtube.com/@manomaytaxinc.?si=IR4t0ZifyisnfpZJ',
      color: 'hover:bg-red-50 hover:text-red-600',
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions about filing your taxes? Ready to get started? We're here to help you get the refund you deserve.
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* HubSpot Contact Form */}
            <div>
              <Card className="shadow-xl border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you within 24-48 hours
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  {/* HubSpot Form Container - Using exact embed code format */}
                  <div className="min-h-[400px]">
                    {!formLoaded && (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
                        <p className="text-gray-600">Loading form...</p>
                      </div>
                    )}
                    
                    {/* HubSpot form using data attributes method */}
                    <div 
                      className="hs-form-frame" 
                      data-region="na3" 
                      data-form-id="fbb000c3-03a8-4cd2-9147-c0f472292b18" 
                      data-portal-id="342909001"
                      data-testid="hubspot-form-container"
                    ></div>
                  </div>
                  
                  <p className="text-sm text-gray-500 text-center mt-6">
                    We respect your privacy. All information is kept strictly confidential.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {contactInfo.map((info, index) => (
                    <Card key={index} className="border-2 hover:border-blue-300 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                            {info.icon}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                            {info.link ? (
                              <a
                                href={info.link}
                                className="text-blue-600 hover:text-blue-700 transition-colors"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {info.content}
                              </a>
                            ) : (
                              <p className="text-gray-600">{info.content}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Connect With Us</h2>
                <div className="space-y-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center space-x-4 p-4 border-2 rounded-lg transition-all ${social.color}`}
                    >
                      <div className="p-2 rounded-lg">
                        {social.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold">{social.name}</h3>
                        <p className="text-sm text-gray-600">{social.handle}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Response Guarantee */}
              <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white border-0">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-4">Quick Response Guarantee</h3>
                  <p className="text-blue-100 mb-6">
                    We understand that tax matters can be urgent. That's why we guarantee a response 
                    to all inquiries within 24-48 hours during business days.
                  </p>
                  <div className="flex items-center space-x-3">
                    <Clock className="h-8 w-8" />
                    <div>
                      <p className="font-semibold text-lg">24-48 Hour Response</p>
                      <p className="text-sm text-blue-100">Monday - Friday</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp CTA */}
              <Card className="bg-green-50 border-2 border-green-200">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Need Immediate Assistance?</h3>
                  <p className="text-gray-700 mb-6">
                    Chat with us directly on WhatsApp for quick questions and support
                  </p>
                  <Button asChild size="lg" className="bg-green-500 hover:bg-green-600 text-white w-full">
                    <a href="https://wa.me/message/E7OKL6FXEAG6L1" target="_blank" rel="noopener noreferrer">
                      <Phone className="mr-2 h-5 w-5" />
                      Chat on WhatsApp
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Statement */}
      <section className="py-12 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Privacy Matters</h3>
          <p className="text-gray-600">
            All information you share with Manomay Tax Inc. is kept strictly confidential. 
            We never share your personal or business information with third parties without your explicit consent. 
            Your trust is our priority.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
